import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import { Controller, FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/app/hooks/auth-provider';
import toast from '@/app/toast.ts';
import { AccountFlagEnum } from '@/app/types/account-flag.type.ts';
import { UserTypeEnum } from '@/app/types/user-type.ts';
import { Description, Label } from '@/primitives/fieldset.tsx';
import { Subheading } from '@/primitives/heading.tsx';
import { Switch, SwitchField } from '@/primitives/switch.tsx';
import { usePostAccountMutation } from '@/slices/account-api-slice.ts';
import { useFetchOrganizationQuery } from '@/slices/organization-api-slice.ts';
import type { AccountModel } from '../../../types/account.type.ts';
import AccountFormFields from './account-form-fields.tsx';

const NewAccount = () => {
  const { organizationKey } = useParams();
  const { data } = useFetchOrganizationQuery(organizationKey);
  const { user, isImpersonating } = useAuth();
  const canSetDemoFlag = user?.userType === UserTypeEnum.Admin && !isImpersonating;
  const account = {
    organization: {
      id: data?.id,
    },
    accountFlagId: AccountFlagEnum.None,
  } as AccountModel;

  const navigate = useNavigate();
  const form = useForm<AccountModel>({
    values: {
      ...account,
      specialties: account?.specialties ?? [],
    },
    criteriaMode: 'all',
    resetOptions: {
      keepErrors: false,
    },
  });

  const { control, handleSubmit } = form;
  const [persist, { isLoading }] = usePostAccountMutation();

  const onSubmit: SubmitHandler<AccountModel> = (value: AccountModel) => {
    const newValue = {
      ...value,
      accountFlagId: canSetDemoFlag ? value.accountFlagId : AccountFlagEnum.None,
    };
    persist(newValue)
      .unwrap()
      .then(() => {
        toast.success('Account Saved!');
        navigate(-1);
      })
      .catch(() => toast.error('Error saving account'));
  };

  return (
    <FormProvider {...form}>
      <div className='flex items-center gap-2 mb-4'>
        <Button isIconOnly variant='flat' color='primary' onPress={() => navigate(-1)}>
          <ArrowLeftIcon className='size-4' />
        </Button>
        <h4 className='text-primary-600' onClick={() => toast.success('New Account')}>
          New Account
        </h4>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='mt-4 flex-col flex max-w-screen-lg self-center w-full'
      >
        <Subheading>Add Account for Organization: {data?.name}</Subheading>
        <AccountFormFields account={account} />
        {canSetDemoFlag && (
          <div className='mt-8 max-w-screen-lg self-center w-full rounded-2xl border border-zinc-200 bg-zinc-50/80 p-5'>
            <SwitchField>
              <Label>Create as demo account</Label>
              <Description>
                Hidden from representative search. You can remove the demo flag later from account
                settings.
              </Description>
              <Controller
                name='accountFlagId'
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Switch
                    color='amber'
                    size='lg'
                    onChange={isDemo =>
                      onChange(isDemo ? AccountFlagEnum.Demo : AccountFlagEnum.None)
                    }
                    onBlur={onBlur}
                    checked={value === AccountFlagEnum.Demo}
                  />
                )}
              />
            </SwitchField>
          </div>
        )}
        <div className='mt-4'>
          <Button size='lg' isLoading={isLoading} color='primary' variant='solid' type='submit'>
            Create Account
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default NewAccount;
